import React from "react";
import _ from "lodash";
import classes from "./EventFooter.scss";
import { Button, Tooltip, Avatar } from "@toddle-edu/ds-web";
import classNames from "classnames";
import {
  isNewQuickViewEditFFEnabled,
  getCanUserJoinMeeting,
} from "../../modules/utils";
import { ArrowOutOutlined } from "@toddle-edu/ds-icons";

const styles = {
  avatarStyles: {
    size: "medium",
  },
  overlayInnerStyle: { maxWidth: 160, textAlign: "center" },
  viewTaskButtonStyle: { zIndex: 1 },
};

const EventFooter = React.memo(props => {
  const {
    createdBy,
    onConfirmBtnClick,
    itemType,
    isQuickReschedule,
    isButtonDisabled,
    isNotUserCourse,
    t,
    canJoin = true,
    testId,
    isCurrentAcademicYear,
    isUserStaff,
    errorMsgLocalesRef,
    assignmentStatus,
    isVideoMeeting,
    isVideoMeetingRevampEnabled,
    isParent,
    sharedWith,
    associatedEntities,
    users,
    meetingLink,
  } = props;

  const { id, name, profileImage, eventCreatorName } = createdBy;

  const isNewQuickViewEnabled = isNewQuickViewEditFFEnabled();
  const getButtonLabel = () => {
    if (isQuickReschedule) {
      return t("common:update");
    }
    if (itemType === "CALENDAR_EVENT_ITEM") {
      return t("calendar:join_now");
    }
    if (isUserStaff) {
      if (isNewQuickViewEnabled && assignmentStatus !== "PUBLISHED") {
        return t("calendar:view_task");
      }
      return t("calendar:view_and_evaluate");
    }
    return isNewQuickViewEnabled ? t("calendar:view_task") : t("common:view");
  };

  const label = getButtonLabel();

  const avatarShape = "square";

  const getButtonVariant = () => {
    if (isQuickReschedule) {
      return "primary";
    }
    if (isNewQuickViewEnabled) {
      return "neutral";
    }
    return "progressive";
  };

  const isVideoMeetingAndRevampEnabled =
    isVideoMeeting && isVideoMeetingRevampEnabled;

  const hasMeetingLink = !_.isEmpty(meetingLink);

  const canUserJoinMeeting = getCanUserJoinMeeting({
    isParent,
    sharedWith,
    associatedEntities,
    users,
  });

  return (
    <div className={classes.footer}>
      <div className={classes.leftFooter}>
        <div className={classes.profile}>
          <Avatar
            shape={avatarShape}
            size={"medium"}
            name={eventCreatorName || ""}
            id={id}
            src={profileImage || ""}
            dsVersion={"2.0"}
          />
          <div className={classes.nameContainer}>
            <div className={"text-body-s"}>
              {isUserStaff
                ? t("common:created_by")
                : itemType === "CALENDAR_EVENT_ITEM"
                ? t("common:invited_by")
                : t("classRoom:assigned_by")}
            </div>
            <div
              className={classNames("text-label", classes.nameTextContainer)}
            >
              {name}
            </div>
          </div>
        </div>
      </div>

      {/* This button is only used for meetings when videoMeetingRevamp is enabled */}
      {isVideoMeetingAndRevampEnabled &&
      canUserJoinMeeting &&
      (isQuickReschedule || hasMeetingLink) ? (
        <Button
          variant={isQuickReschedule ? "primary" : "progressive"}
          type={"fill"}
          size={"medium"}
          onClick={onConfirmBtnClick}
          style={styles.viewTaskButtonStyle}
          testId={`${testId}-${label}`}
          dsVersion={"2.0"}
          shortcut={{ code: "Enter", shiftKey: true }}
        >
          {isQuickReschedule ? t("common:update") : t("common:join_meeting")}
        </Button>
      ) : null}

      {/*
       * This buttons will be used in the following cases:
       * isVideoMeeting true and isVideoMeetingRevampEnabled false | For old video meetings, these buttons will be used
       * isVideoMeeting false and isVideoMeetingRevampEnabled true | For Assignments, in any case, these buttons will be used
       */}
      {!isVideoMeetingAndRevampEnabled && canJoin && isCurrentAcademicYear ? (
        <div className={classes.rightFooter}>
          <Tooltip
            trigger={
              isNotUserCourse && !isNewQuickViewEnabled ? ["hover"] : [null]
            }
            tooltip={t("classRoom:cannot_access_class_task_sentence_case")}
            overlayInnerStyle={styles.overlayInnerStyle}
            placement={"top"}
          >
            {isNewQuickViewEnabled ? (
              <Tooltip
                trigger={
                  isButtonDisabled || isNotUserCourse ? ["hover"] : [null]
                }
                tooltip={t(errorMsgLocalesRef.current)}
                overlayInnerStyle={styles.overlayInnerStyle}
                placement={"top"}
                dsVersion="2.0"
                showArrow={true}
              >
                <Button
                  variant={getButtonVariant()}
                  type={isQuickReschedule ? "fill" : "outlined"}
                  size={"medium"}
                  onClick={onConfirmBtnClick}
                  disabled={isButtonDisabled || isNotUserCourse}
                  style={styles.viewTaskButtonStyle}
                  testId={`${testId}-${label}`}
                  dsVersion={"2.0"}
                  shortcut={{ code: "Enter", shiftKey: true }}
                  rightIcon={!isQuickReschedule ? <ArrowOutOutlined /> : null}
                >
                  {label}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant={getButtonVariant()}
                type={"fill"}
                size={"medium"}
                onClick={onConfirmBtnClick}
                disabled={isButtonDisabled || isNotUserCourse}
                style={styles.viewTaskButtonStyle}
                testId={`${testId}-${label}`}
                dsVersion={"2.0"}
                shortcut={{ code: "Enter", shiftKey: true }}
              >
                {label}
              </Button>
            )}
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
});

EventFooter.displayName = "EventFooter";

EventFooter.defaultProps = {
  isButtonDisabled: false,
};

export default EventFooter;
